import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown, Info, Search } from 'lucide-react';
import { toast } from 'react-toastify';
import { useEPMContext } from '../../../contexts/EPMContext';
import { useUserContext } from '../../../contexts/UserContext';
import { getAllEpmLearnings } from '../../../utils/APIs';
import { formatTestCardId } from '../../../utils/converter';
import TestPeekModal from '../TestingBoard/TestPeekModal';
import { fmtDay, keyToTest, testId } from '../epmHelpers';
import Select from '../../UI/Select';
import Button from '../../UI/Button/Button';
import Badge from '../../UI/Badge/Badge';
import Input from '../../UI/Input';

const VERDICTS = ['any', 'win', 'loss', 'inconclusive'];

const verdictClass = {
    win: 'bg-emerald-50 text-emerald-700 border border-emerald-200/80 font-bold',
    loss: 'bg-rose-50 text-rose-700 border border-rose-200/80 font-bold',
    inconclusive: 'bg-amber-50 text-amber-800 border border-amber-200/80 font-bold',
    invalid: 'bg-slate-100 text-slate-700 border border-slate-200 font-bold',
};

const roleLabel = { origin: 'ORIGIN', supports: 'SUPPORTS', contradicts: 'CONTRADICTS' };

const idOf = (ref) => String(ref?._id || ref || '');
const daysSince = (date) => (date ? Math.max(0, Math.round((Date.now() - new Date(date).getTime()) / 86400000)) : 0);

/**
 * An epm_learnings record in the shape the cards read. Verdict and links come from the tests that reference it:
 * it is the origin learning of a test that holds it at learning_ids[0].
 */
const fromDbLearning = (doc, tests, tagsMap) => {
    const id = idOf(doc._id);
    const linked = (tests || []).filter((test) => (test.learning_ids || []).some((ref) => idOf(ref) === id));
    const isOrigin = (test) => idOf(test.learning_ids?.[0]) === id;
    const origin = linked.find(isOrigin) || linked[0];
    const versions = doc.versions || [];

    return {
        id,
        belief: doc.current_assumption || '',
        verdict: (origin?.verdict?.status || '').toLowerCase(),
        uplift: '',
        scope: doc.applies_to || '',
        scopeNote: '',
        confidence: (doc.confidence || 'Low').toLowerCase(),
        version: versions.length || 1,
        daysAgo: daysSince(doc.updatedAt || doc.createdAt),
        believed: doc.assumption_made || '',
        happened: doc.outcome || '',
        why: doc.current_assumption_reason || '',
        next: doc.next_tests || '',
        tags: (doc.tag_ids || []).map((tag) => tagsMap?.[idOf(tag)]?.name).filter(Boolean),
        recordedBy: doc.created_by_name || doc.created_by || '',
        links: linked.map((test) => ({
            role: isOrigin(test) ? 'origin' : 'supports',
            key: formatTestCardId(test),
            testId: idOf(testId(test)),
            detail: [test.verdict?.status, test.status?.toLowerCase()].filter(Boolean).join(' · '),
        })),
        revisions: versions.map((entry, i) => ({
            version: i + 1,
            daysAgo: daysSince(entry.recorded_at),
            who: entry.recorded_by || 'Unknown',
            note: entry.note || '',
        })),
    };
};

const Field = ({ label, children }) => {
    if (!children) return null;
    return (
        <div className="flex flex-col gap-1 text-xs">
            <span className="font-semibold text-gray-400 uppercase tracking-wider text-[11px]">{label}</span>
            <p className="m-0 text-gray-800 leading-relaxed font-normal">{children}</p>
        </div>
    );
};

const LearningCard = ({ learning, isOpen, onToggle, onOpenTest, tests }) => {
    const verdictStyle = verdictClass[learning.verdict] || verdictClass.inconclusive;
    return (
        <article className="stride-card-interactive overflow-hidden">
            <button
                type="button"
                onClick={onToggle}
                className="w-full p-5 text-left border-0 bg-transparent flex items-start gap-3.5 cursor-pointer focus:outline-none"
                aria-expanded={isOpen}
            >
                <ChevronDown size={18} className={`text-primary transition-transform duration-150 mt-1 shrink-0 ${isOpen ? '' : '-rotate-90'}`} />
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                        {learning.verdict && (
                            <span className={`px-2 py-0.5 rounded text-[11px] uppercase tracking-wider ${verdictStyle}`}>
                                {learning.verdict}
                            </span>
                        )}
                        {learning.confidence && (
                            <span className="text-xs text-gray-500 font-medium">
                                {learning.confidence} confidence
                            </span>
                        )}
                        <span className="text-xs text-gray-400">· v{learning.version} · {fmtDay(-learning.daysAgo)}</span>
                    </div>
                    <h3 className="m-0 text-sm md:text-base font-bold text-stride-heading leading-snug">
                        {learning.belief}
                    </h3>
                </div>
            </button>

            {isOpen && (
                <div className="px-5 pb-5 pl-[48px] flex flex-col gap-5 border-t border-gray-50 pt-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                        <Field label="What we believed">{learning.believed}</Field>
                        <Field label="What happened">{learning.happened}</Field>
                        <Field label="Why we think that">{learning.why}</Field>
                        <Field label="What to test next">{learning.next}</Field>
                    </div>

                    <div className="flex flex-wrap gap-2 items-center">
                        {learning.tags.map((tag) => (
                            <Badge key={tag} variant="neutral" appearance="soft" size="xs">
                                {tag}
                            </Badge>
                        ))}
                        {learning.scope && (
                            <span className="text-xs text-gray-500 ml-2">
                                Applies to: {learning.scope}{learning.scopeNote ? ` · ${learning.scopeNote}` : ''}
                            </span>
                        )}
                    </div>

                    <div>
                        <div className="text-[11px] text-gray-400 uppercase tracking-wider font-bold mb-2">
                            Linked tests — {learning.links.length}
                        </div>
                        {learning.links.length > 0 && (
                            <div className="border border-gray-100 rounded-lg divide-y divide-gray-100">
                                {learning.links.map((link) => {
                                    const test = link.testId
                                        ? (tests || []).find((t) => idOf(testId(t)) === link.testId)
                                        : keyToTest(tests, link.key);
                                    return (
                                        <div key={`${link.role}-${link.key}`} className="flex items-center gap-4 px-4 py-2.5 text-xs">
                                            <span className="w-[92px] text-[10px] font-bold tracking-wider text-gray-400 uppercase">
                                                {roleLabel[link.role]}
                                            </span>
                                            <span className="flex-1 text-stride-heading">
                                                <strong>{link.key}</strong> {test?.name}
                                                {link.detail && <span className="text-gray-400"> · {link.detail}</span>}
                                            </span>
                                            {test && (
                                                <Button
                                                    variant="link"
                                                    size="xs"
                                                    onClick={() => onOpenTest(String(testId(test)))}
                                                >
                                                    Open
                                                </Button>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    <div>
                        <div className="text-[11px] text-gray-400 uppercase tracking-wider font-bold mb-2">
                            Revision history — {learning.revisions.length} · append-only
                        </div>
                        {learning.revisions.map((revision) => (
                            <div key={revision.version} className="flex gap-4 text-xs text-gray-600 py-1">
                                <span className="w-[28px] font-semibold text-gray-800">v{revision.version}</span>
                                <span className="w-[64px] text-gray-400">{fmtDay(-revision.daysAgo)}</span>
                                <span className="w-[120px] font-medium">{revision.who}</span>
                                <span className="flex-1 text-gray-500">{revision.note}</span>
                            </div>
                        ))}
                    </div>

                    {learning.recordedBy && (
                        <div className="text-xs text-gray-400">
                            Recorded by {learning.recordedBy}
                        </div>
                    )}
                </div>
            )}
        </article>
    );
};

export const Learnings = () => {
    const { tests = [], tagsMap = {} } = useEPMContext();
    const { userData } = useUserContext();
    const [dbLearnings, setDbLearnings] = useState([]);
    const [query, setQuery] = useState('');
    const [tags, setTags] = useState([]);
    const [verdict, setVerdict] = useState('any');
    const [openId, setOpenId] = useState(null);
    const [peekTestId, setPeekTestId] = useState(null);
    const closePeek = useCallback(() => setPeekTestId(null), []);

    useEffect(() => {
        let mounted = true;
        const orgToken = userData?.selectedOrganization?.org_token;
        const userToken = userData?.token;
        if (!userToken || !orgToken) return;

        getAllEpmLearnings(userToken, orgToken)
            .then((res) => {
                if (!mounted) return;
                const list = res?.data?.learnings || res?.data || [];
                setDbLearnings(Array.isArray(list) ? list : []);
            })
            .catch((err) => {
                toast.error(err?.message || 'Failed to load learnings');
            });

        return () => {
            mounted = false;
        };
    }, [userData?.token, userData?.selectedOrganization?.org_token]);

    const learnings = useMemo(() => {
        return dbLearnings.map((doc) => fromDbLearning(doc, tests, tagsMap));
    }, [dbLearnings, tests, tagsMap]);

    const tagOptions = useMemo(
        () => Array.from(new Set(learnings.flatMap((l) => l.tags))).sort(),
        [learnings],
    );

    const toggleTag = (tag) => setTags((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]));

    const results = useMemo(() => {
        const text = query.trim().toLowerCase();
        return learnings.filter((learning) => {
            if (verdict !== 'any' && learning.verdict !== verdict) return false;
            if (tags.length && !tags.every((tag) => learning.tags.includes(tag))) return false;
            if (!text) return true;
            return [learning.belief, learning.believed, learning.happened, learning.why, learning.next, ...learning.links.map((l) => l.key)]
                .some((value) => String(value || '').toLowerCase().includes(text));
        });
    }, [learnings, query, tags, verdict]);

    const filtered = query || tags.length || verdict !== 'any';

    return (
        <main className="stride-page-shell space-y-6">
            <header className="mb-6">
                <p className="stride-eyebrow">Experiment programme</p>
                <h1 className="text-2xl md:text-3xl stride-heading">Learnings</h1>
                <span className="text-xs md:text-sm text-stride-muted mt-1 block">
                    Every concluded test leaves a learning here. It stays true because it is written as part of the work, not as a separate wiki.
                </span>
            </header>

            <div className="stride-card rounded-2xl overflow-hidden">
                <div className="p-6 flex flex-col gap-4 border-b border-border bg-surface/80">
                    <Input
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search beliefs, results and tests — e.g. sticky cta"
                        prefixIcon={<Search size={16} className="text-gray-400" />}
                        clearable
                        onClear={() => setQuery('')}
                    />
                    <div className="flex flex-wrap items-center gap-2">
                        {tagOptions.length > 0 && <span className="text-xs font-semibold text-gray-500 mr-1">Tags:</span>}
                        {tagOptions.map((tag) => (
                            <button
                                key={tag}
                                type="button"
                                onClick={() => toggleTag(tag)}
                                className={`px-3 py-1 rounded-full text-xs font-medium border cursor-pointer transition-all ${
                                    tags.includes(tag)
                                        ? 'bg-primary border-primary text-white shadow-xs'
                                        : 'bg-white border-border text-stride-body hover:border-border-hover hover:bg-surface'
                                }`}
                            >
                                {tag}{tags.includes(tag) ? ' ✕' : ''}
                            </button>
                        ))}
                        <span className="flex-1" />
                        <span className="text-xs font-semibold text-gray-500">Verdict:</span>
                        <Select
                            value={verdict}
                            onChange={(e) => setVerdict(e.target.value)}
                            options={VERDICTS.map((v) => ({ value: v, label: v.charAt(0).toUpperCase() + v.slice(1) }))}
                            className="m-0 min-w-[130px]"
                            selectClassName="!py-1.5 !px-3 !text-xs capitalize !border-gray-200 !bg-white !text-gray-800 rounded-lg"
                        />
                        {filtered && (
                            <Button
                                variant="link"
                                size="xs"
                                onClick={() => { setQuery(''); setTags([]); setVerdict('any'); }}
                            >
                                Clear all
                            </Button>
                        )}
                    </div>
                </div>

                <div className="p-6 flex flex-col gap-4">
                    <div className="flex items-center gap-2 text-xs text-gray-500 font-medium">
                        <strong className="text-stride-heading font-bold">{filtered ? results.length : learnings.length} of {learnings.length} learnings</strong>
                        <span title="Ideation reads this library on capture ('3 similar tests have run here'), and Prioritisation reads win rates by tag and real effect sizes as Confidence anchors.">
                            <Info size={14} className="text-gray-400" />
                        </span>
                        <span className="ml-auto text-gray-400">Sort: relevance</span>
                    </div>
                    {results.map((learning) => (
                        <LearningCard
                            key={learning.id}
                            learning={learning}
                            isOpen={openId === learning.id}
                            onToggle={() => setOpenId(openId === learning.id ? null : learning.id)}
                            onOpenTest={setPeekTestId}
                            tests={tests}
                        />
                    ))}
                    {!results.length && (
                        <div className="text-center text-xs text-gray-400 py-12">
                            {filtered ? 'No learnings match. Try fewer filters.' : 'No learnings yet. Concluding a test records its learning here.'}
                        </div>
                    )}
                </div>
            </div>
            {peekTestId && <TestPeekModal testId={peekTestId} onClose={closePeek} />}
        </main>
    );
};

export default Learnings;
